import { useCallback } from 'react';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import SetTaskStatusSuccessMutation from '../__generated__/SetTaskStatusSuccessMutation.graphql.ts';
import type {
	PageUpdateTrigger,
	SetTaskStatusSuccessMutation$data,
	SetTaskStatusSuccessMutation$variables,
} from '../__generated__/SetTaskStatusSuccessMutation.graphql.ts';

export type CompleteTaskTrigger = NonNullable<PageUpdateTrigger>;

interface UseCompleteTaskOptions {
	trigger?: CompleteTaskTrigger;
}

interface UseCompleteTaskResult {
	loading: boolean;
	error: Error | undefined;
	completeTask: (contentId: string, taskId: string) => Promise<void>;
}

/**
 * Hook to mark a task as complete via the inline task GraphQL mutation.
 */
export function useCompleteTask({
	trigger = 'VIEW_PAGE',
}: UseCompleteTaskOptions = {}): UseCompleteTaskResult {
	const [commitMutation, { loading, error }] = useMutationInterop<
		SetTaskStatusSuccessMutation$data,
		SetTaskStatusSuccessMutation$variables
	>(SetTaskStatusSuccessMutation);

	const completeTask = useCallback(
		async (contentId: string, taskId: string) => {
			const response = await commitMutation({
				variables: {
					input: {
						cid: contentId,
						taskId,
						status: 'CHECKED',
						trigger,
					},
				},
			});

			if (!response.data?.setTaskStatus?.success) {
				throw new Error('Failed to complete task');
			}
		},
		[commitMutation, trigger],
	);

	return { completeTask, loading, error: error as Error | undefined };
}
